// ─────────────────────────────────────────────────────────────────────────────
// French locale — fr.ts  (Canadian French / fr-CA)
// ─────────────────────────────────────────────────────────────────────────────

import { Locale } from './en';

const fr: Locale = {
  realEstateInsights: { title: 'Détails de la propriété', hint: 'Explorez la maison et ses environs',
    structure: 'Toiture, maison, arbres et surfaces aménagées', parcel: 'Limites, dimensions et superficie du terrain',
    sun: 'Suivez le soleil au fil des heures et des saisons', neighborhood: 'Comparez les maisons voisines, les arbres et les obstacles à la vue',
    privacy: 'Explorez les surfaces extérieures visibles depuis les fenêtres voisines',
    outlook: 'Explorez les arbres voisins et les obstacles à la vue', terrain: 'Explorez la carte des hauteurs et la pente du terrain' },
  sunPath: { title: 'Trajectoire du soleil', date: 'Date', localTime: 'Heure de Medical Lake · Pacifique', sunrise: 'Lever', sunset: 'Coucher',
    play: 'Animer la journée', pause: 'Pause', close: 'Fermer la trajectoire', today: "Aujourd’hui", summer: 'Été', winter: 'Hiver',
    elevation: 'Hauteur du soleil', direction: 'Direction', notice: 'Lumière illustrative · Ombres et météo non simulées.',
    unavailable: 'Lever ou coucher du soleil indisponible pour cette date.', west: 'O', fit: 'Cadrer la trajectoire solaire',
    lowSun: 'Soleil bas · lumière chaude et douce', midSun: 'Soleil incliné · lumière douce', highSun: 'Soleil haut · lumière plus vive' },
  pins: {
    assets: 'Ressources de repères', loading: 'Chargement des repères…', failed: 'Impossible de charger les repères.', retry: 'Réessayer',
    visible: 'Afficher les repères', size: 'Taille des repères', opacity: 'Opacité', colorBy: 'Couleur selon',
    uniform: 'Couleur unique', all: 'Tous', reset: 'Effacer les filtres', view: 'Aller à la vue enregistrée',
    selected: 'Repère sélectionné', close: 'Réduire les détails', count: (shown, total) => `${shown.toLocaleString('fr-CA')} / ${total.toLocaleString('fr-CA')} repères`,
    load: 'Afficher les repères', invalid: 'Le fichier de repères est invalide ou non pris en charge.', selectHint: 'Cliquez sur un repère pour le consulter.',
  },
  // ── GeoAutomationViewer ────────────────────────────────────────────────────
  viewer: {
    // Error states
    errorTitle:          'Erreur de chargement',
    errorBack:           'Retour',
    errorMissingScene:   'Paramètre URL manquant : scene',
    errorNoManifest:     (name: string) => `La scène '${name}' n'a pas d'URL de manifeste`,
    errorNotFound:       (id: string)   => `Aucune scène trouvée pour l'identifiant : ${id}`,
    errorInit:           'Impossible d\'initialiser le visualiseur',
    errorLoad3D:         'Impossible de charger la scène 3D',

    // Loading
    loading3D:           'Chargement du modèle 3D...',
    preparingModel:      'Préparation du modèle 3D...',
    startingViewer:      'Démarrage du visualiseur 3D...',
    downloadingModel:    (pct: number) => `Téléchargement du modèle 3D : ${pct} %`,
    processingModel:     'Traitement du modèle 3D...',
    loadingModelLabel:   (label: string) => `Chargement du modèle 3D : ${label}`,

    // Toolbar buttons
    assetsBtn:           'Éléments',
    sourceImagesBtn:     'Images sources',
    measureBtn:          'Mesure',
    selectionBtn:        'Sélectionner',
    selectionBtnTitle:   'Sélectionner un contexte pour la requête',
    imageViewBtn:        'Vue image',
    imageViewTitle:      'Basculer en vue image',
    imageViewLoading:    'Chargement du modèle 3D…',
    segmentLoading:      'Chargement du segment…',

    // Branding
    poweredBy:           'Propulsé par',

    // Navigation overlay
    navTitle:            'Navigation',
    navLookAround:       'Regarder autour',
    navLookHint:         'Cliquer & glisser',        // glisser = drag in QC UI
    navMove:             'Se déplacer',
    navMoveHint:         'Touches fléchées',         // fléchées vs directionnelles in QC
    navAdvanced:         'Navigation avancée :',
    navUp:               'Monter',
    navDown:             'Descendre',
  },

  // ── MeasureTool ───────────────────────────────────────────────────────────
  measure: {
    // Mode labels
    modePoint:           'Point',
    modeLine:            'Ligne',
    modePolygon:         'Polygone',

    // Instruction text
    instructClickFirst:  'Cliquez sur un premier point dans la scène',
    instructPointPlaced: 'Point placé — cliquez sur Terminer ou ajoutez-en d\'autres',
    instructAddMore:     (n: number) => `${n} points — cliquez pour en ajouter`,

    // Empty state
    clickToPlace:        'Cliquez dans la scène pour placer des points',

    // Stat labels
    statLength:          'Longueur',
    statCoordinates:     'Coordonnées',
    statPerimeter:       'Périmètre',
    statArea:            'Superficie',
    statSegmentSlopes:   'Pentes par segment',
    statNoElevation:     'Aucun changement d\'élévation détecté',  // élévation vs altitude in QC engineering

    // Slope panel
    slopeAngleSlope:     'pente',
    slopeAngleVertical:  'vertical',
    slopeAngleBase:      'base',
    slopeRun:            'Dist. horiz.',
    slopeRise:           'Dénivelé',
    slopeGrade:          'Gradient',                // standard QC civil engineering term
    slopePointC:         'Point C (sol sous H)',

    // Buttons
    btnCompletePolygon:  'Fermer le polygone',
    btnAnalyzeSlope:     '⊿ Analyser la pente',
    btnAnalyzeHeight:    'Analyser la hauteur',
    btnAnalyzeWidth:     'Analyser la largeur',
    btnClearAll:         'Tout effacer',
    btnDone:             'Terminer',
    modeWidth:           'Largeur',
    statSlope:           'Pente',
    statPerpWidth:       'Largeur perpendiculaire',
    widthGuideHint:      "Glisser A' ou A\" pour aligner",   // glisser = drag in QC
    widthPickedLineNote: 'La ligne tracée représente déjà la largeur perpendiculaire.',
    widthDescription:    'Largeur = distance minimale de B à la droite passant par A',
    widthComputeError:   'Impossible de calculer la largeur pour ce guide.',
  },

  // ── PoiNavigationOverlay ──────────────────────────────────────────────────
  poiNav: {
    navigatingTo:        'Navigation vers',
    loadingScene:        'Chargement des données à proximité…',
    selectedPoint:       'Point sélectionné',
  },

  // ── POINotification ───────────────────────────────────────────────────────
  poiNotif: {
    veryClose:           'TRÈS PROCHE',
    nearby:              'À PROXIMITÉ',
    ahead:               'DEVANT',
    goBtn:               'Y aller',
  },

  // ── SourceImages ──────────────────────────────────────────────────────────
  sourceImages: {
    zoomReset:           'réinit.',
    scrollToZoom:        'roulette pour zoomer',              // roulette = scroll wheel in QC
    scrollZoomDragPan:   'roulette pour zoomer · glisser pour naviguer',
    seedQuestion:        'Quelle vue correspond le mieux à votre point ?',
    seedHint:            "Choisissez l'angle — on trouvera d'autres vues depuis cette perspective",
    pickDifferentPoint:  'Choisir un autre point',
    close:               'Fermer',
    prev:                'Préc.',
    selectThisView:      'Choisir cette vue',
    more:                'Plus',
    moreLeft:            (n: number) => `${n} restants`,
    loadingImages:       'Chargement…',
    backToViews:         '← Retour aux vues',
    clickToZoom:         'cliquer sur la vignette pour zoomer',
    finding:             'Recherche des images sources…',
    clickInstruction:    'Cliquez sur l\'objet dans la scène pour trouver les images sources',
  },

  viewerAgent: {
    phLoading:            'Chargement du visualiseur...',
    phPlanning:           'Analyse en cours...',
    phAsk:                'Posez une question sur la scène...',
    cancelled:            'Requête arrêtée. Que souhaitez-vous demander à la place ?',
    waitingFromStatus:    (status: string) => `En attente de l'Intelligence Spatiale : ${status}`,
    waitingBroadAnalysis: "Analyse globale de la scène en cours — cela peut prendre quelques minutes.",
    stillWorking:         'Traitement des données de reconstruction…',
    takingLonger:         "Analyse spatiale en cours — cela peut prendre un moment.",
    spatialAnalysis:      'Analyse spatiale',
    spatialAnalysisFiles: (n: number) => `Analyse spatiale · ${n} fichier${n !== 1 ? 's' : ''} produit${n !== 1 ? 's' : ''}`,
    handingOff:           "Transfert vers l'Intelligence Spatiale...",
    connectingSI:         "Connexion à l'Intelligence Spatiale",
    siError:              (msg: string) => `Erreur de l'Intelligence Spatiale : ${msg}`,
    connStoppedWithText:  "La connexion s'est interrompue avant la réponse finale. Vous pouvez continuer avec un suivi ou réessayer.",
    connStoppedNoText:    "La connexion s'est interrompue avant la réponse finale. Veuillez réessayer ou poser une question plus précise.",
    preview:              'Aperçu',
    download:             'Télécharger',
    pendingResultsReady:  'Votre demande précédente a peut-être des résultats prêts :',
    checkNow:             'Vérifier maintenant',
    checkResultsPrompt:   (q: string) => `Vérifier les résultats de ma demande précédente : ${q}`,
    dismiss:              'Ignorer',
    outputFile:           'Fichier de sortie',
    welcomeQuestion:      'Que pouvez-vous faire dans cette scène ?',
    runningTool:          (name: string) => `Exécution : ${name}`,
    siTurnComplete:       'Tour d\'Intelligence Spatiale terminé.',
    siAnalysisComplete:   'Analyse d\'Intelligence Spatiale terminée.',
    hideHistory:          'Masquer les messages précédents',
    viewHistory:          'Voir les messages précédents',
    openInViewer:         'Ouvrir dans le visualiseur',
    detectedFeatures:     'Entités détectées — faites glisser les coins pour corriger',
    flyingTo:             (label: string) => `Navigation vers ${label}`,
    openChat:             'Ouvrir le chat',
    viewIn3D:             'Voir en 3D',
    cancelAriaLabel:      'Annuler',
    sendAriaLabel:        'Envoyer',
    toggleLangAriaLabel:  'Changer de langue',
    minimizeChat:         'Réduire le chat',
    selectionDoneGeneric: 'J\'ai sélectionné une zone — que souhaitez-vous faire ici ?',
    thinkingPhrases: [
      'Interrogation des données de reconstruction…',
      'Lecture de la géométrie de la scène…',
      'Vérification de la base de connaissances…',
      'Analyse spatiale en cours…',
      'Examen de la disposition des objets…',
      'Préparation de la réponse…',
    ],
    paddingStages: [
      'Analyse des données de reconstruction…',
      'Localisation des éléments dans la scène…',
      'Calcul des coordonnées spatiales…',
      'Recoupement avec la base de connaissances…',
      'Préparation de la réponse…',
    ],
  },
  artifact: {
    openTab:            'Ouvrir l\'onglet',
    notBrowserReadable: 'Ce chemin de sortie n\'est pas encore lisible par le navigateur.',
    filesystemPathNote: 'L\'agent a renvoyé un chemin de fichier. Le serveur doit exposer ce fichier comme URL HTTP avant qu\'il puisse être prévisualisé dans le navigateur.',
  },
  sceneLabels: {
    hiddenOne:  (text: string) => `L'étiquette « ${text} » est hors du champ de vue actuel.`,
    hiddenMany: (count: number) => `${count} étiquettes sont hors du champ de vue actuel.`,
  },
  reconstructionAgent: {
    title:            'Reco',
    openChat:         'Ouvrir Reco',
    minimize:         'R\u00e9duire',
    toggleLang:       'Changer de langue',
    restartSession:   'Recommencer',
    expandChat:       'Ouvrir en pleine page',
    collapseChat:     'Retour au site',
    send:             'Envoyer',
    placeholder:      '\u00c9crivez \u00e0 Reco\u2026',

    welcome:          'Je suis Reco. Je vous aide \u00e0 reconstruire des mod\u00e8les spatiaux 3D '
                      + '\u00e0 partir de vos propres photos ou vid\u00e9os.',
    selectToProceed:   'Commencez ici',
    optionOwnData:     'Ma propre capture',
    optionOwnDataHint: 'Photos ou vid\u00e9os que vous avez prises',
    optionOwnDataMsg:  'J\u2019ai mes propres images ou vid\u00e9os \u00e0 t\u00e9l\u00e9verser.',
    optionAddress:     'Une adresse',
    optionAddressHint: 'Nous fournissons l\u2019imagerie a\u00e9rienne',
    optionAddressMsg:  'Je veux reconstruire un lieu \u00e0 partir d\u2019une adresse.',

    uploadIntro:    'Partagez un lien vers vos images.',
    uploadHosts:    'Drive, Dropbox, WeTransfer',
    uploadPick:     'Choisir un dossier d\u2019images',
    uploadProgress: (done, total) => `T\u00e9l\u00e9versement ${done} sur ${total}\u2026`,
    uploadDone:     (count) => `${count} images t\u00e9l\u00e9vers\u00e9es`,
    uploadFailed:   '\u00c9chec du t\u00e9l\u00e9versement',
    uploadClear:    'Retirer le t\u00e9l\u00e9versement',

    addressIntro:       'Saisissez l\u2019adresse \u00e0 reconstruire.',
    addressPlaceholder: 'ex. 600 Montgomery St, San Francisco',
    extentIntro:        'S\u00e9lectionnez la zone \u00e0 reconstruire',
    extentCheckCoverage: 'V\u00e9rifier la couverture',
    coverageDecline:    'Annuler',
    previewVerifying:   'Nous v\u00e9rifions rapidement que les images a\u00e9riennes conviennent '
                      + '\u00e0 une reconstruction 3D. Si tout est bon, vous pourrez examiner '
                      + 'quelques images d\u2019exemple avant d\u2019approuver la reconstruction.',
    previewReadyMsg:    'V\u00e9rifi\u00e9\u00a0! Voici des images d\u2019exemple \u00e0 examiner. Si elles vous '
                      + 'conviennent, je peux lancer la reconstruction 3D.',
    previewUnsuitable:  'Malheureusement, ces images ne conviennent pas \u00e0 une reconstruction. Nous '
                      + 'travaillerons avec notre partenaire Vexcel pour r\u00e9gler ce probl\u00e8me.\n\n',
    previewExpired:     'L\u2019aper\u00e7u des images a expir\u00e9. V\u00e9rifions \u00e0 nouveau la '
                      + 'couverture pour que je puisse vous montrer des images r\u00e9centes.\n\n',
    previewForbidden:   'Malheureusement, ces images ne sont pas encore autoris\u00e9es pour les '
                      + 'reconstructions. Nous travaillerons avec notre partenaire Vexcel pour r\u00e9gler ce probl\u00e8me.\n\n',
    newRequestDivider:  'Nouvelle demande',
    namePlaceholder:    'Nom et pr\u00e9nom',
    companyPlaceholder: 'Soci\u00e9t\u00e9',
    emailRequiredMark:  'obligatoire',
    emailWhyCancel:     'Pas de souci. Qu\u2019est-ce qui vous a fait renoncer\u00a0?',
    cancelEmailNoEmail: 'Je pr\u00e9f\u00e8re ne pas donner d\u2019adresse e-mail',
    cancelEmailTrying:  'Je voulais juste essayer le chat',
    cancelEmailOther:   'Autre chose',
    previewWhyCancel:   'Pas de souci. Qu\u2019est-ce qui vous a fait renoncer\u00a0?',
    previewCancelThanks: 'Merci, c\u2019est utile. Souhaitez-vous essayer un autre lieu ou utiliser '
                      + 'votre propre capture\u00a0?',
    previewStart:       '\u00c7a me va',
    cancelled:          'Annul\u00e9',
    cancelledNoEmail:   'Je ne souhaite pas donner d\u2019adresse e-mail. Annulez la reconstruction.',
    cancelReasonQuality: 'Qualit\u00e9 des images',
    cancelReasonCoverage: 'Couverture insuffisante',
    cancelReasonOther:  'Autre chose',
    emailPlaceholder:   'vous@entreprise.com',
    emailStart:         'Lancer la reconstruction',
    emailRequired:      'Il me faut une adresse e-mail avant de lancer.',
    emailInvalid:       'Cela ne ressemble pas \u00e0 une adresse e-mail.',
    coverageEmailAsk:   'Je suis pr\u00eat \u00e0 lancer votre reconstruction, qui peut prendre un certain '
                      + 'temps. Pour continuer, il me faut une adresse e-mail afin de vous envoyer '
                      + 'l\u2019avancement et le lien vers le r\u00e9sultat.',
    extentStart:        'Lancer la reconstruction',
    extentChecking:     'V\u00e9rification de l\u2019imagerie\u2026',
    cancel:             'Annuler',

    warningProceed: 'Continuer quand m\u00eame',
    warningRevise:  'Envoyer un meilleur jeu de donn\u00e9es',
    warningWorking: 'En cours\u2026',

    locatingAddress:     'Recherche de cette adresse\u2026',
    systemChecking:      'V\u00e9rification de votre jeu de donn\u00e9es',
    checkingPlaceholder: 'V\u00e9rification de votre jeu de donn\u00e9es\u2026',
    awaitingConfirm:     'Confirmez ou annulez ci-dessus\u2026',
    statusChecking:      'V\u00e9rification',
    statusProcessing:    'Traitement',
    statusDone:          'Re\u00e7ue',
    doneTitle:           'Demande re\u00e7ue',
    doneNoteWorldMap:    'Nous lan\u00e7ons votre reconstruction. Nous vous enverrons un e-mail '
                       + 'quand votre mod\u00e8le sera pr\u00eat.',
    startOver:          'On recommence\u00a0?',
    extentSelected:     'J\u2019ai s\u00e9lectionn\u00e9 la zone sur la carte.',
    previewAccepted:    'Les aper\u00e7us sont bons. Lancez la reconstruction 3D.',
    doneNote:            'Nous v\u00e9rifions votre jeu de donn\u00e9es. Nous vous enverrons un e-mail quand '
                         + 'votre mod\u00e8le sera pr\u00eat.',
    filesGenerated:      'Fichiers g\u00e9n\u00e9r\u00e9s',
    errorRetry:          'Impossible de joindre Reco. R\u00e9essayez.',
  },
};

export default fr;
